import configData from './appConfig.json';

/**
 * Application configuration
 *
 * Base values are loaded from appConfig.json
 * Critical values can be overridden by environment variables
 *
 * @see appConfig.json for default values
 */

export const POLLING_CONFIG = {
  /**
   * Interval for event aggregator polling (ms)
   * @default 5000
   * @envvar VITE_POLL_INTERVAL
   */
  EVENT_AGGREGATOR_INTERVAL:
    parseInt(import.meta.env.VITE_POLL_INTERVAL) ||
    configData.polling.eventAggregatorInterval,

  /**
   * Delay between API retry attempts (ms)
   * @default 200
   * @envvar VITE_API_RETRY_DELAY
   */
  API_RETRY_DELAY:
    parseInt(import.meta.env.VITE_API_RETRY_DELAY) ||
    configData.polling.apiRetryDelay,

  /**
   * Delay between batch operations (ms)
   * @default 100
   * @envvar VITE_BATCH_DELAY
   */
  BATCH_OPERATION_DELAY:
    parseInt(import.meta.env.VITE_BATCH_DELAY) ||
    configData.polling.batchOperationDelay,
};

export const UI_TIMINGS = {
  /**
   * Camera flash animation duration (ms)
   * @default 120
   */
  CAMERA_FLASH_DURATION: configData.uiTimings.cameraFlashDuration,

  /**
   * Map marker selection animation duration (ms)
   * @default 600
   */
  MAP_MARKER_ANIMATION: configData.uiTimings.mapMarkerAnimation,

  /**
   * New event indicator display duration (ms)
   * @default 3000
   */
  NEW_EVENT_INDICATOR: configData.uiTimings.newEventIndicator,

  /**
   * Poll pulse animation duration (ms)
   * @default 1000
   */
  POLL_PULSE_DURATION: configData.uiTimings.pollPulseDuration,

  /**
   * Swipe animation cleanup delay (ms)
   * @default 100
   */
  SWIPE_ANIMATION_CLEANUP: configData.uiTimings.swipeAnimationCleanup,

  /**
   * Match callback delay (ms)
   * @default 50
   */
  MATCH_CALLBACK_DELAY: configData.uiTimings.matchCallbackDelay,
};

export const IMAGE_CONFIG = {
  /**
   * Maximum image size in megabytes
   * @default 7
   * @envvar VITE_IMAGE_MAX_SIZE_MB
   */
  MAX_SIZE_MB:
    parseInt(import.meta.env.VITE_IMAGE_MAX_SIZE_MB) ||
    configData.imageProcessing.maxSizeMB,

  /**
   * Initial compression quality (0.0 - 1.0)
   * @default 0.9
   * @envvar VITE_IMAGE_QUALITY
   */
  INITIAL_QUALITY:
    parseFloat(import.meta.env.VITE_IMAGE_QUALITY) ||
    configData.imageProcessing.initialQuality,

  /**
   * Quality reduction step per iteration
   * @default 0.1
   */
  QUALITY_STEP: configData.imageProcessing.qualityStep,
};

export const API_CONFIG = {
  /**
   * Default limits for API queries
   */
  QUERY_LIMITS: {
    /**
     * Maximum events per aggregated query
     * Backend enforces MAX_LIMIT=100
     * @default 100
     */
    AGGREGATED_EVENTS: 100,

    /**
     * Maximum events per polling query
     * @default 20
     */
    POLLING_EVENTS: 20,

    /**
     * Maximum events per pagination page
     * @default 20
     */
    PAGE_SIZE: 20,
  },

  /**
   * React Query cache configuration
   */
  CACHE: {
    /**
     * How long data is considered fresh (ms)
     * @default 30000 (30 seconds)
     */
    STALE_TIME: 30000,

    /**
     * How long unused data stays in cache (ms)
     * @default 300000 (5 minutes)
     */
    CACHE_TIME: 300000,

    /**
     * Polling interval for real-time updates (ms)
     * @default 10000 (10 seconds)
     */
    REFETCH_INTERVAL: 10000,
  },
};

/**
 * Event type visibility defaults
 * Used across Event History and Event Aggregator
 */
export const DEFAULT_VISIBLE_EVENT_TYPES = {
  match: true,
  soi_match: true,
  multi_dataset_match: true,
  group_match: true,
  promotion: true,
  assignment: true,
  note: true,
  detection: true,
  soi_event: true,
  unknown_promotion: true,
};

export const USER_SETTINGS_DEFAULTS = {
  /**
   * Camera settings defaults
   */
  CAMERA: {
    REMOVE_BG: configData.userSettings.camera.removeBg,
    SLIDER_VALUE: configData.userSettings.camera.sliderValue,
    MULTI_FACE_MODE: configData.userSettings.camera.multiFaceMode,
    SHOW_BOUNDING_BOXES: configData.userSettings.camera.showBoundingBoxes,
    IMMEDIATE_FACE_MATCH: configData.userSettings.camera.immediateFaceMatch,
    COMPRESSION_QUALITY: configData.userSettings.camera.compressionQuality,
    AUTO_ADJUST_COMPRESSION: configData.userSettings.camera.autoAdjustCompression,
    IS_INDOOR: configData.userSettings.camera.isIndoor,
  },

  /**
   * Face matching settings defaults
   */
  FACE_MATCH: {
    UPDATE_CHARACTERISTICS_ON_MATCH: configData.userSettings.faceMatch.updateCharacteristicsOnMatch,
    AUTO_CREATE_EVENT_ON_AI_MATCH: configData.userSettings.faceMatch.autoCreateEventOnAIMatch,
  },

  /**
   * Location settings defaults
   */
  LOCATION: {
    /**
     * Use GPS for high accuracy location
     * @default true
     */
    HIGH_ACCURACY: configData.userSettings.location.highAccuracy,

    /**
     * Timeout for location acquisition (ms)
     * @default 10000
     */
    ACQUISITION_TIMEOUT: configData.userSettings.location.acquisitionTimeout,

    /**
     * Maximum age of cached location (ms)
     * 0 = always get fresh location
     * @default 0
     */
    CACHE_MAX_AGE: configData.userSettings.location.cacheMaxAge,
  },

  /**
   * History/Event settings defaults
   */
  HISTORY: {
    /**
     * Delete confirmation settings for each event type
     * @default { match: false, assignment: true }
     */
    DELETE_CONFIRMATION: configData.userSettings.history.deleteConfirmation,

    /**
     * Auto-enable voice control
     * @default false
     */
    AUTO_ENABLE_VOICE: configData.userSettings.history.autoEnableVoice,

    /**
     * Stealth mode enabled by default
     * @default false
     */
    STEALTH_MODE: configData.userSettings.history.stealthMode,
  },

  /**
   * Aggregator settings defaults
   */
  AGGREGATOR: {
    /**
     * Maximum events to keep in memory for aggregator
     * Prevents performance degradation with infinite scroll
     * @default 500
     */
    EVENT_LIMIT: configData.userSettings.aggregator.eventLimit,
  },

  /**
   * Video recording settings defaults
   */
  VIDEO: {
    /**
     * Enable stealth video capture feature
     * @default false
     */
    ENABLED: false,

    /**
     * Frame extraction interval in seconds
     * Min: 3, Max: 60
     * @default 3
     */
    FRAME_INTERVAL: 3,

    /**
     * Maximum recording duration in seconds
     * @default 300 (5 minutes)
     */
    MAX_DURATION: 300,

    /**
     * Save video to S3 (placeholder for future feature)
     * @default false
     */
    SAVE_TO_S3: false,

    /**
     * Auto-stop recording after first match
     * @default false
     */
    AUTO_STOP_ON_MATCH: false,

    /**
     * Show bounding boxes on video preview
     * @default true
     */
    SHOW_BOUNDING_BOXES: true,
  },
};
